import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import axios from 'axios';
import { toast } from 'sonner';
import { Skull, Check, X, Lock, CalendarBlank, Trophy } from '@phosphor-icons/react';
import PrizeBanner from '../components/PrizeBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WCLastManStandingPage() {
  const { t } = useTranslation();
  const [matchDays, setMatchDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [matches, setMatches] = useState([]);
  const [picks, setPicks] = useState([]);
  const [status, setStatus] = useState(null);
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [daysRes, picksRes, statusRes, standingsRes] = await Promise.all([
        axios.get(`${API}/wc-lms/match-days`),
        axios.get(`${API}/wc-lms/picks`),
        axios.get(`${API}/wc-lms/status`),
        axios.get(`${API}/wc-lms/standings`),
      ]);
      setMatchDays(daysRes.data);
      setPicks(picksRes.data);
      setStatus(statusRes.data);
      setStandings(standingsRes.data);

      if (daysRes.data.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const upcoming = daysRes.data.find(d => d.date >= today) || daysRes.data[0];
        setSelectedDay(upcoming.date);
      }
    } catch (err) {
      console.error('Failed to fetch WC LMS data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDay) {
      axios.get(`${API}/wc-lms/matches?lms_round=${selectedDay}`)
        .then(res => setMatches(res.data))
        .catch(() => setMatches([]));
    }
  }, [selectedDay]);

  const existingPick = picks.find(p => (p.lms_round || p.match_date) === selectedDay);

  const handlePick = async () => {
    if (!selectedMatch || !selectedTeam) {
      toast.error('Select a match and a team to win');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/wc-lms/pick`, { match_id: selectedMatch, team_id: selectedTeam });
      toast.success('Pick saved!');
      setSelectedMatch(null);
      setSelectedTeam(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save pick');
    } finally {
      setSubmitting(false);
    }
  };

  const isMatchLocked = (match) => {
    const matchDt = new Date(`${match.match_date}T${match.match_time || '15:00'}:00Z`);
    const lockDt = new Date(matchDt.getTime() - 24 * 60 * 60 * 1000);
    return new Date() >= lockDt;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="wc-lms-page">
      <Navigation />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Skull size={32} weight="bold" className="text-accent" />
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white" data-testid="wc-lms-title">
              {t('wcLms.title')}
            </h1>
          </div>
          <p className="text-text-secondary text-base">
            {t('wcLms.subtitle')}
          </p>
        </div>

        {/* Prize Banner */}
        <div className="mb-6">
          <PrizeBanner variant="compact" />
        </div>

        {/* Status Banner */}
        {status?.eliminated && (
          <div className="bg-accent/10 border border-accent/30 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="wc-lms-eliminated">
            <Skull size={24} className="text-accent flex-shrink-0" />
            <div>
              <p className="text-accent font-bold">{t('wcLms.eliminated')}</p>
              <p className="text-text-secondary text-sm">{t('wcLms.eliminatedOn')} {status.eliminated_on}</p>
            </div>
          </div>
        )}

        {!status?.eliminated && status?.total_picks > 0 && (
          <div className="bg-primary/10 border border-primary/30 rounded-sm p-4 mb-6 flex items-center gap-3" data-testid="wc-lms-alive">
            <Check size={24} className="text-primary flex-shrink-0" weight="bold" />
            <div>
              <p className="text-primary font-bold">{t('wcLms.alive')}</p>
              <p className="text-text-secondary text-sm">{status.correct_picks} {t('wcLms.correctOf')} {status.total_picks}</p>
            </div>
          </div>
        )}

        {matchDays.length === 0 ? (
          <div className="bg-background-paper border border-border rounded-sm p-12 text-center" data-testid="wc-lms-no-matches">
            <CalendarBlank size={48} className="text-text-muted mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-white mb-2">{t('wcLms.noMatches')}</h2>
            <p className="text-text-secondary">{t('wcLms.noMatchesDesc')}</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Match Days sidebar */}
            <div className="bg-background-paper border border-border rounded-sm p-4" data-testid="wc-lms-match-days">
              <h2 className="font-heading text-lg font-bold text-white mb-3 flex items-center gap-2">
                <CalendarBlank size={20} className="text-[#FFD700]" />
                {t('wcLms.matchDays')}
              </h2>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {matchDays.map(day => {
                  const dayPick = picks.find(p => (p.lms_round || p.match_date) === day.date);
                  const isSelected = selectedDay === day.date;
                  return (
                    <button
                      key={day.date}
                      onClick={() => setSelectedDay(day.date)}
                      data-testid={`match-day-${day.date}`}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-sm text-left transition-all text-sm ${
                        isSelected
                          ? 'bg-primary text-black font-bold'
                          : 'text-white/80 hover:bg-white/5'
                      }`}
                    >
                      <span className="font-mono">{new Date(day.date + 'T12:00:00Z').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${isSelected ? 'text-black/60' : 'text-text-muted'}`}>{day.match_count} {t('wcLms.games')}</span>
                        {dayPick && (
                          dayPick.status === 'correct' ? <Check size={16} className="text-primary" weight="bold" /> :
                          dayPick.status === 'wrong' ? <X size={16} className="text-accent" weight="bold" /> :
                          <span className="w-2 h-2 bg-[#FFD700] rounded-full" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Matches for selected day */}
            <div className="lg:col-span-2 space-y-4">
              {/* Current pick for this day */}
              {existingPick && (
                <div className="bg-background-paper border border-[#FFD700]/30 rounded-sm p-4" data-testid="wc-lms-current-pick">
                  <p className="text-xs uppercase tracking-wide text-[#FFD700] font-bold mb-2">{t('wcLms.yourPick')} {selectedDay}</p>
                  <div className="flex items-center gap-3">
                    {existingPick.team?.flag && (
                      <img src={existingPick.team.flag} alt="" className="w-8 h-5 object-cover rounded-sm" />
                    )}
                    <span className="text-white font-medium">{existingPick.team?.name || 'Unknown'}</span>
                    {existingPick.status === 'correct' && <span className="text-primary text-xs font-bold uppercase ml-auto">{t('wcLms.correct')}</span>}
                    {existingPick.status === 'wrong' && <span className="text-accent text-xs font-bold uppercase ml-auto">{t('wcLms.wrong')}</span>}
                    {existingPick.status === 'pending' && <span className="text-[#FFD700] text-xs font-bold uppercase ml-auto">{t('wcLms.pending')}</span>}
                  </div>
                </div>
              )}

              {/* Matches */}
              <div className="space-y-3">
                {matches.map(match => {
                  const locked = isMatchLocked(match) || match.status === 'completed';
                  const isPickedMatch = selectedMatch === match.id;
                  return (
                    <div
                      key={match.id}
                      data-testid={`wc-match-${match.id}`}
                      className={`bg-background-paper border rounded-sm p-4 transition-all ${
                        isPickedMatch ? 'border-primary' : 'border-border'
                      } ${locked ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs uppercase tracking-wide text-text-muted">{match.stage}{match.group ? ` - ${match.group}` : ''}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono text-text-muted">{match.match_time} UTC</span>
                          {locked && <Lock size={14} className="text-text-muted" />}
                        </div>
                      </div>

                      {match.status === 'completed' ? (
                        <div className="flex items-center justify-center gap-6">
                          <div className="flex items-center gap-2">
                            {match.home_team?.flag && <img src={match.home_team.flag} alt="" className="w-8 h-5 object-cover rounded-sm" />}
                            <span className={`text-sm font-medium ${match.winner_team_id === match.home_team_id ? 'text-primary font-bold' : 'text-white'}`}>{match.home_team?.name}</span>
                          </div>
                          <span className="font-mono text-xl font-bold text-white">{match.home_score} - {match.away_score}</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${match.winner_team_id === match.away_team_id ? 'text-primary font-bold' : 'text-white'}`}>{match.away_team?.name}</span>
                            {match.away_team?.flag && <img src={match.away_team.flag} alt="" className="w-8 h-5 object-cover rounded-sm" />}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {/* Home team button */}
                          <button
                            disabled={locked || status?.eliminated}
                            onClick={() => { setSelectedMatch(match.id); setSelectedTeam(match.home_team_id); }}
                            data-testid={`pick-home-${match.id}`}
                            className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-sm transition-all ${
                              selectedMatch === match.id && selectedTeam === match.home_team_id
                                ? 'bg-primary text-black font-bold border border-primary'
                                : 'bg-background-subtle border border-border hover:border-primary/50 text-white'
                            } ${locked || status?.eliminated ? 'cursor-not-allowed' : ''}`}
                          >
                            {match.home_team?.flag && <img src={match.home_team.flag} alt="" className="w-6 h-4 object-cover rounded-sm" />}
                            <span className="text-sm truncate">{match.home_team?.name}</span>
                          </button>

                          <span className="text-text-muted text-xs font-bold px-2">{t('wcLms.vs')}</span>

                          {/* Away team button */}
                          <button
                            disabled={locked || status?.eliminated}
                            onClick={() => { setSelectedMatch(match.id); setSelectedTeam(match.away_team_id); }}
                            data-testid={`pick-away-${match.id}`}
                            className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-sm transition-all ${
                              selectedMatch === match.id && selectedTeam === match.away_team_id
                                ? 'bg-primary text-black font-bold border border-primary'
                                : 'bg-background-subtle border border-border hover:border-primary/50 text-white'
                            } ${locked || status?.eliminated ? 'cursor-not-allowed' : ''}`}
                          >
                            {match.away_team?.flag && <img src={match.away_team.flag} alt="" className="w-6 h-4 object-cover rounded-sm" />}
                            <span className="text-sm truncate">{match.away_team?.name}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Submit pick */}
              {selectedMatch && selectedTeam && !status?.eliminated && (
                <button
                  onClick={handlePick}
                  disabled={submitting}
                  data-testid="wc-lms-submit-pick"
                  className="w-full py-4 bg-primary hover:bg-primary-hover text-black font-bold uppercase tracking-wide text-lg rounded-sm transition-all hover:scale-[1.01] glow-effect disabled:opacity-50"
                >
                  {submitting ? t('wcLms.saving') : (existingPick ? t('wcLms.updatePick') : t('wcLms.lockInPick'))}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Standings */}
        {standings.length > 0 && (
          <div className="mt-8 bg-background-paper border border-border rounded-sm p-5" data-testid="wc-lms-standings">
            <h2 className="font-heading text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy size={22} weight="bold" className="text-[#FFD700]" />
              {t('wcLms.standings')}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-text-muted uppercase tracking-wide text-xs">
                    <th className="py-2 px-3 text-left">#</th>
                    <th className="py-2 px-3 text-left">{t('wcLms.player')}</th>
                    <th className="py-2 px-3 text-center">{t('wcLms.correct')}</th>
                    <th className="py-2 px-3 text-center">{t('wcLms.total')}</th>
                    <th className="py-2 px-3 text-center">{t('wcLms.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((entry, i) => (
                    <tr key={entry.user_id} className="border-b border-border/50 hover:bg-white/5">
                      <td className="py-3 px-3 font-mono font-bold text-text-muted">{i + 1}</td>
                      <td className="py-3 px-3 text-white">{entry.display_name || entry.email?.split('@')[0]}</td>
                      <td className="py-3 px-3 text-center font-mono text-primary font-bold">{entry.correct_picks}</td>
                      <td className="py-3 px-3 text-center font-mono text-text-secondary">{entry.total_picks}</td>
                      <td className="py-3 px-3 text-center">
                        {entry.eliminated
                          ? <span className="text-accent text-xs font-bold uppercase">{t('wcLms.eliminatedLabel')}</span>
                          : <span className="text-primary text-xs font-bold uppercase">{t('wcLms.aliveLabel')}</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
